<template>
  <div>
    <v-card>
      <SkeletonTable
              :loading="loading"
              :first-load="firstLoad"
              :cols="4"
              :rows="15"
      />
      <v-data-table
        :headers="headers"
        :items="projects"
        :search="search"
        :custom-filter="filterItems"
        :loading="loading"
        :loading-text="$t('table.loading')"
        v-show="!firstLoad"
        :items-per-page="15"
        :footer-props="{
          itemsPerPageOptions: [15, 30, -1],
          itemsPerPageText: $t('table.itemsPerPageText')
        }"
        sort-by="start_date"
        :sort-desc="true"
        class="table-cursor"
      >
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('search')"
              single-line
              hide-details
            />
            <v-menu offset-y :close-on-content-click="false" max-width="380">
              <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                <v-tooltip top>
                  <template v-slot:activator="{ on: tooltipOn }">
                    <v-btn icon class="mx-2" :color="search ? 'orange' : 'grey'"
                      v-bind="menuAttrs" v-on="{ ...menuOn, ...tooltipOn }" :aria-label="$t('filters')">
                      <v-icon>{{ search ? 'mdi-filter-check' : 'mdi-filter-outline' }}</v-icon>
                    </v-btn>
                  </template>
                  {{ $t('filters') }}
                </v-tooltip>
              </template>
              <v-card class="project-filter-menu">
                <v-card-title class="subtitle-1">{{ $t('filters') }}</v-card-title>
                <v-card-text class="pt-3 pb-0">
                  <v-select v-for="group in filterGroups" :key="group.key"
                    :label="$t('project.list.' + group.key)" :items="filterOptions(group)"
                    :value="selectedFilter(group)" @change="setFilter(group, $event)"
                    dense outlined hide-details class="mb-3" />
                  <v-text-field v-for="field in ['year', 'code', 'doi']" :key="field"
                    :label="$t('project.list.' + field)" :value="filterValue(field)"
                    @change="setTextFilter(field, $event)" clearable dense outlined hide-details class="mb-3" />
                </v-card-text>
                <v-card-actions>
                  <v-btn text small @click="search = ''">{{ $t('project.list.clear') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
            <v-spacer />
            <v-btn
              color="primary"
              dark
              class="mb-2"
              to="/manage/edit/project/add"
              ><v-icon class="mr-2">mdi-plus-thick</v-icon
              >{{ $t("button.newItem") }}</v-btn
            >
          </v-toolbar>
        </template>
        <template
          v-slot:item.title="{ item }"
        >
          <span v-if="item.title['gl'] !== '' && item.title['gl'] !== undefined">{{item.title['gl']}}</span>
          <span v-else-if="item.title['es'] !== '' && item.title['es'] !== undefined">{{item.title['es']}}</span>
          <span v-else>{{item.title['en']}}</span>
        </template>
        <template v-slot:item.type="{ item }">
          {{ $t(`project.types.${item.type}`) }}
        </template>
        <template
          v-slot:item.features="{ item }"
        >
          <v-tooltip v-for="feature in featureDefinitions" :key="feature.key" top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" :aria-label="featureLabel(feature, item)"
                :color="featureColor(feature, item)" small class="ma-1">
                {{ feature.icons[hasFeature(item, feature.key) ? 0 : 1] }}
              </v-icon>
            </template>
            {{ featureLabel(feature, item) }}
          </v-tooltip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon text color="primary" :to="`/manage/edit/project/edit/${item.id}`"
                v-bind="attrs" v-on="on" :aria-label="$t('project.list.edit')">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
            {{ $t('project.list.edit') }}
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon color="red" v-bind="attrs" v-on="on"
                :aria-label="$t('delete')" @click="deleteItem(item)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            {{ $t('delete') }}
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
import SkeletonTable from "../../components/reusables/SkeletonTable";
import { projectFeatureDefinitions, projectFeatures, matchesProjectSearch } from "../../components/projects/projectListFilters";
export default {
  components: {SkeletonTable},
  data() {
    return {
      dialog: false,
      loading: true,
      firstLoad: true,
      search: "",
      projects: [],
      featureDefinitions: projectFeatureDefinitions
    };
  },

  watch: {
    dialog(val) {
      val || this.close();
    }
  },
  computed: {
    filterGroups() {
      return this.featureDefinitions.concat(['areas', 'code', 'doi'].map(key => ({ key, yes: `has-${key}`, no: `no-${key}` })));
    },
    headers() {
      return [
        {
          text: this.$t("form.title"),
          value: "title",
          width: "30%"
        },
        {
          text: this.$t("table.acronym"),
          value: "acronym"
        },
        {
          text: this.$t("type"),
          value: "type"
        },
        {
          text: this.$t("form.project.start_date"),
          value: "start_date"
        },
        {
          text: this.$t("form.features"),
          value: "features",
          filterable: false,
          width: "25%",
          sortable: false
        },
        {
          text: this.$t("actions"),
          value: "actions",
          align: "end",
          width: "15%",
          filterable: false,
          sortable: false
        }
      ];
    }
  },
  created() {
    this.initialize();
  },

  methods: {
    initialize() {
      let path = this.$store.state.endpoint + "/project";
      axios
        .get(path)
        .then(response => {
          this.projects = response.data;
          this.loading = false;
          if (this.firstLoad) this.firstLoad = false;
        })
        .catch(error => {
          console.log(error);
        });
    },
    deleteItem(item) {
      if (confirm(this.$t("message.confirm.delete"))) {
        let path = this.$store.state.endpoint + "/project/" + item.id;
        axios
          .delete(path)
          .then(response => {
            const index = this.projects.indexOf(item);
            this.projects.splice(index, 1);
            this.$store.commit("alert", {
              color: "success",
              text: this.$t("message.correct.deleteItem")
            });
          })
          .catch(error => {
            console.log(error);
            this.$store.commit("alert", {
              color: "error",
              text: error.response
            });
          });
      }
    },
    hasFeature(item, key) { return projectFeatures(item)[key]; },
    featureLabel(feature, item) {
      return this.$t(`project.list.${feature.key}${this.hasFeature(item, feature.key) ? 'Yes' : 'No'}`);
    },
    featureColor(feature, item) {
      const present = this.hasFeature(item, feature.key);
      if (['description', 'objectives', 'results', 'image'].includes(feature.key)) return present ? 'grey' : feature.color;
      return present ? feature.color : 'grey';
    },
    filterOptions(group) {
      return [
        { text: this.$t('project.list.all'), value: '' },
        { text: this.$t(`project.list.${group.key}Yes`), value: group.yes },
        { text: this.$t(`project.list.${group.key}No`), value: group.no }
      ];
    },
    searchTerms() { return this.search.split(',').map(term => term.trim()).filter(Boolean); },
    selectedFilter(group) { return this.searchTerms().find(term => [group.yes, group.no].includes(term)) || ''; },
    setFilter(group, value) {
      this.search = this.searchTerms().filter(term => ![group.yes, group.no].includes(term)).concat(value || []).filter(Boolean).join(', ');
    },
    filterValue(field) { return (this.searchTerms().find(term => term.startsWith(field + ':')) || '').slice(field.length + 1); },
    setTextFilter(field, value) {
      const terms = this.searchTerms().filter(term => !term.startsWith(field + ':'));
      if (value && String(value).trim()) terms.push(`${field}:${String(value).trim()}`);
      this.search = terms.join(', ');
    },
    filterItems(value, search, item) { return matchesProjectSearch(item, search); }
  }
};
</script>

<style scoped>
.project-filter-menu { width: 360px; max-width: calc(100vw - 24px); }
.project-filter-menu .v-card__text { max-height: 65vh; overflow-y: auto; }
</style>
